import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Footprints, User, Lock, Loader2, UserPlus, Key, Sparkles, Shield, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const [isFirstUser, setIsFirstUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [requiresTotp, setRequiresTotp] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    totp_code: ""
  });
  
  useEffect(() => {
    checkFirstUser();
  }, []);
  
  const checkFirstUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/check`);
      setIsFirstUser(response.data.first_user);
      if (response.data.first_user) {
        setIsRegistering(true);
      }
    } catch (error) {
      console.error("Error checking first user:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      toast.error("Inserisci username e password");
      return;
    }
    
    if (isRegistering && !formData.name) {
      toast.error("Inserisci il tuo nome");
      return;
    }
    
    if (requiresTotp && !formData.totp_code) {
      toast.error("Inserisci il codice 2FA");
      return;
    }
    
    setSubmitting(true);
    
    try {
      if (isRegistering) {
        const response = await axios.post(`${API}/auth/register`, {
          username: formData.username,
          password: formData.password,
          name: formData.name
        }, { withCredentials: true });
        
        toast.success("Account admin creato!");
        navigate("/setup-2fa");
      } else {
        const response = await axios.post(`${API}/auth/login`, {
          username: formData.username,
          password: formData.password,
          totp_code: formData.totp_code || undefined
        }, { withCredentials: true });
        
        if (response.data.requires_totp) {
          setRequiresTotp(true);
          toast.info("Inserisci il codice 2FA");
          setSubmitting(false);
          return;
        }
        
        if (response.data.requires_2fa_setup) {
          navigate("/setup-2fa");
          return;
        }
        
        toast.success("Accesso effettuato");
        navigate("/dashboard", { state: { user: response.data.user } });
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Errore di autenticazione";
      toast.error(message);
      if (requiresTotp) {
        setFormData(prev => ({ ...prev, totp_code: "" }));
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="relative">
          <div className="w-12 h-12 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
          <div className="absolute inset-0 w-12 h-12 border-2 border-transparent border-b-emerald-500/50 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex relative overflow-hidden bg-slate-950">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Dark gradient base */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"></div>
        
        {/* Animated mesh gradient */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 -left-40 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-[128px] animate-pulse"></div>
          <div className="absolute top-1/3 -right-40 w-96 h-96 bg-emerald-600 rounded-full mix-blend-multiply filter blur-[128px] animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute -bottom-20 left-1/3 w-96 h-96 bg-violet-600 rounded-full mix-blend-multiply filter blur-[128px] animate-pulse" style={{ animationDelay: '4s' }}></div>
        </div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '64px 64px'
        }}></div>
        
        {/* Floating particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 5}s`
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Left Side - Branding */}
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 relative z-10">
        {/* Logo and branding */}
        <div>
          <div className="flex items-center gap-4 mb-16">
            <div className="relative group">
              <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-2xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity"></div>
              <div className="relative w-14 h-14 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-2xl">
                <Footprints className="w-7 h-7 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                OrthoTrack
              </h1>
              <p className="text-sm text-slate-400">Sistema Gestionale Avanzato</p>
            </div>
          </div>
          
          {/* Feature highlights */}
          <div className="space-y-8 max-w-md">
            <h2 className="text-4xl font-bold text-white leading-tight tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Gestisci le tue pratiche in modo{' '}
              <span className="bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
                intelligente
              </span>
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed">
              Una piattaforma completa per la gestione delle pratiche ortopediche, finanziarie e amministrative.
            </p>
            
            {/* Features list */}
            <div className="space-y-4 pt-4">
              {[
                { icon: Shield, text: "Sicurezza avanzata con 2FA", color: "text-blue-400" },
                { icon: Sparkles, text: "Interfaccia moderna e intuitiva", color: "text-emerald-400" },
                { icon: ArrowRight, text: "Gestione multi-area integrata", color: "text-violet-400" }
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3 group">
                  <div className={`w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center ${feature.color} group-hover:bg-white/10 transition-colors`}>
                    <feature.icon className="w-5 h-5" />
                  </div>
                  <span className="text-slate-300 group-hover:text-white transition-colors">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Version badge */}
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-700 to-transparent"></div>
          <span className="text-xs text-slate-500 font-mono tracking-wider">
            Versione 1.0 - 14/02/2026
          </span>
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-700 to-transparent"></div>
        </div>
      </div>
      
      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative z-10">
        <div className="w-full max-w-md" data-testid="login-container">
          {/* Glass card */}
          <div className="relative">
            {/* Card glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 via-emerald-500/20 to-violet-500/20 rounded-3xl blur-xl"></div>
            
            <div className="relative backdrop-blur-xl bg-white/[0.08] border border-white/[0.1] rounded-2xl p-8 shadow-2xl">
              {/* Mobile logo */}
              <div className="flex lg:hidden justify-center mb-8">
                <div className="relative">
                  <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-2xl blur-lg opacity-40"></div>
                  <div className="relative w-16 h-16 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-xl flex items-center justify-center">
                    <Footprints className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
              
              {/* Header */}
              <div className="text-center lg:text-left mb-8">
                <h2 className="text-2xl font-bold text-white mb-2 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {isRegistering 
                    ? "Crea Account Admin" 
                    : requiresTotp 
                      ? "Verifica 2FA"
                      : "Bentornato"
                  }
                </h2>
                <p className="text-slate-400">
                  {isRegistering 
                    ? "Configura il primo account amministratore" 
                    : requiresTotp 
                      ? "Inserisci il codice dal tuo authenticator"
                      : "Accedi al tuo account per continuare"
                  }
                </p>
              </div>
              
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {!requiresTotp && (
                  <>
                    {isRegistering && (
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-300 text-sm font-medium">Nome Completo</Label>
                        <div className="relative group">
                          <UserPlus className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                          <Input
                            id="name"
                            name="name"
                            type="text"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Mario Rossi"
                            className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-xl focus:bg-white/10 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
                            data-testid="input-name"
                          />
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="username" className="text-slate-300 text-sm font-medium">Username</Label>
                      <div className="relative group">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                        <Input
                          id="username"
                          name="username"
                          type="text"
                          value={formData.username}
                          onChange={handleChange}
                          placeholder="nome.utente"
                          className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-xl focus:bg-white/10 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
                          autoComplete="username"
                          data-testid="input-username"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-slate-300 text-sm font-medium">Password</Label>
                      <div className="relative group">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="••••••••"
                          className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-xl focus:bg-white/10 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
                          autoComplete={isRegistering ? "new-password" : "current-password"}
                          data-testid="input-password"
                        />
                      </div>
                    </div>
                  </>
                )}
                
                {requiresTotp && (
                  <div className="space-y-2">
                    <Label htmlFor="totp_code" className="text-slate-300 text-sm font-medium">Codice Authenticator</Label>
                    <div className="relative group">
                      <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
                      <Input
                        id="totp_code"
                        name="totp_code"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={formData.totp_code}
                        onChange={(e) => setFormData(prev => ({ ...prev, totp_code: e.target.value.replace(/\D/g, '') }))}
                        placeholder="000000"
                        className="pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-xl text-center text-xl tracking-[0.5em] font-mono focus:bg-white/10 focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                        autoFocus
                        data-testid="input-totp"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setRequiresTotp(false);
                        setFormData(prev => ({ ...prev, totp_code: "" }));
                      }}
                      className="text-sm text-blue-400 hover:text-blue-300 transition-colors mt-2 flex items-center gap-1"
                    >
                      <ArrowRight className="w-3 h-3 rotate-180" />
                      Torna al login
                    </button>
                  </div>
                )}
                
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 active:scale-[0.98]"
                  data-testid="submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {isRegistering ? "Creazione..." : requiresTotp ? "Verifica..." : "Accesso..."}
                    </>
                  ) : (
                    <>
                      {isRegistering ? "Crea Account" : requiresTotp ? "Verifica Codice" : "Accedi"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
              
              {/* Footer text */}
              <p className="text-xs text-slate-500 text-center mt-6">
                {isRegistering 
                  ? "Questo sarà l'account amministratore principale"
                  : requiresTotp
                    ? "Apri Google Authenticator per il codice"
                    : "Accesso riservato agli operatori autorizzati"
                }
              </p>
              
              {/* Mobile version */}
              <div className="lg:hidden mt-8 pt-6 border-t border-white/10">
                <p className="text-xs text-slate-500 text-center font-mono tracking-wider">
                  Versione 1.0 - 14/02/2026
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
